import Bugsnag from "@bugsnag/js";

import { ApiError, isApiError } from "@config/apiClient";
import { REACT_QUERY_ERROR } from "@config/constants";
import { DisconnectedError } from "@app-types/errors";
import { isIgnorableBugsnagEvent } from "@config/bugsnagFilters";
import { normalizeApiPath } from "@utils/strings.util";
import { EMAIL_QUERY_KEY, MEETING_QUERY_KEY } from "@config/query";
import { ERROR_STRINGS } from "@config/messages";
import usePlatformStore from "@store/usePlatformStore";

// get/sync email and get/sync meeting: the call sites where a wrong itemType sends the
// request to the wrong endpoint, so the event needs the item context that produced it.
const ITEM_CALL_SITES = [
  EMAIL_QUERY_KEY,
  MEETING_QUERY_KEY,
  ERROR_STRINGS.SYNC_EMAIL,
  ERROR_STRINGS.SYNC_MEETING,
];

const isItemCallSite = (context?: string) =>
  !!context && ITEM_CALL_SITES.some((site) => context.startsWith(site));

const officeDiagnostics = () => {
  try {
    const office = (globalThis as any).Office?.context;
    return {
      host: office?.mailbox?.diagnostics?.hostName,
      hostVersion: office?.mailbox?.diagnostics?.hostVersion,
      officePlatform: office?.diagnostics?.platform,
    };
  } catch {
    return {};
  }
};

export function useBugsnagErrorHandler() {
  const notifyBugsnag = (error: unknown, context?: string) => {
    if (!Bugsnag.isStarted()) return;
    // skip if token expired
    if ((error as ApiError)?.response?.status === 401) return;
    // skip expected disconnection (403)
    if (error instanceof DisconnectedError) return;

    let normalizedError: Error;

    if (isApiError(error)) {
      normalizedError = new Error(`ApiError: ${error.message} [${error.config?.url}]`);
      (normalizedError as any).response = error.response;
    } else if (error instanceof Error) {
      normalizedError = error;
    } else {
      normalizedError = new Error(typeof error === "string" ? error : "Unknown error");
    }

    normalizedError.name = REACT_QUERY_ERROR; // <-- important
    (normalizedError as any).__fromReactQuery = true; // optional extra marker

    Bugsnag.notify(normalizedError, (event) => {
      // Call site only; raw body lives under `http` below.
      event.addMetadata("react-query", { callSite: context });

      // Item context for the four endpoints keyed on itemType, so a mismatched
      // (itemType, itemId) pair is visible on the event rather than inferred.
      if (isItemCallSite(context)) {
        const { itemId, itemType, prevItemType, isComposeMode, calendarId, platformName } =
          usePlatformStore.getState();
        event.addMetadata("item", {
          itemId,
          itemType,
          prevItemType,
          isComposeMode,
          calendarId,
          platform: platformName,
          ...officeDiagnostics(),
        });
      }

      // Structured HTTP context for the Bugsnag dashboard's HTTP tab and
      // for the onError noise filter to read via event.getMetadata("http").
      // `event.context` shows the URL in the dashboard list view.
      if (isApiError(error) && error.response) {
        event.context = error.config?.url;
        event.addMetadata("http", {
          url: error.config?.url,
          status: error.response.status,
          body: error.response.data,
          method: error.config?.method,
        });

        // Group by status + UUID-normalized path so 400s do not collapse
        // into one "Bad request error (400)" mega-group keyed on the message.
        event.groupingHash = `${error.response.status} ${normalizeApiPath(error.config.url)}`;
      }

      // Runs last on purpose: Bugsnag calls config.onError before this callback, so the
      // App.tsx gate cannot see the metadata the callSite/status filters read.
      if (isIgnorableBugsnagEvent(event)) return false;
    });
  };

  return { notifyBugsnag };
}
